/**
 * /dashboard/settings/leave-types — Settings → Time & Leave → Leave
 * types (SOP §8 + §11.1).
 *
 * Reuses the same hook (useLeaveTypes) and form (LeaveTypeForm) the
 * old /dashboard/leave/admin/types page used; that legacy route now
 * redirects here. Adds the "Restore defaults" button per §11.11.
 *
 * Migration note (2026-06-01, Next.js → Vite Phase 3g): direct port of
 * frontend/src/app/(dashboard)/dashboard/settings/leave-types/page.tsx.
 */

import { useMemo } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { toApiError } from "@/lib/api";
import { useLeaveTypes } from "@/hooks/use-leave";
import { fmtDays } from "@/lib/leave-format";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { LeaveTypeForm } from "@/components/leave/leave-type-form";
import { LeaveSettingsCard } from "@/components/leave/leave-settings-card";
import { SettingsPageHeader } from "@/components/shared/settings-page-header";
import { SettingsShell } from "@/components/shared/settings-shell";
import { SETTINGS_GROUPS } from "@/lib/settings-nav";
import { StatusBadge } from "@/components/shared/status-badge";
import { RestoreDefaultsButton } from "@/components/settings/restore-defaults-button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/settings/leave-types",
)({
  component: SettingsLeaveTypesRoute,
});

function SettingsLeaveTypesRoute() {
  const { isLeaveAdmin } = useLeaveRoles();
  const q = useLeaveTypes();

  const types = useMemo(
    () =>
      [...(q.data?.leave_types ?? [])].sort(
        (a, b) =>
          a.sort_order - b.sort_order || a.code.localeCompare(b.code),
      ),
    [q.data],
  );

  return (
    <>
      <SettingsPageHeader />
      <SettingsShell groups={SETTINGS_GROUPS}>
        <div className="space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-lg font-semibold">Leave types</h2>
              <p className="text-sm text-muted-foreground">
                Configure your organisation&apos;s leave catalog.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {isLeaveAdmin && (
                <RestoreDefaultsButton
                  catalog="leave-types"
                  catalogLabel="leave types"
                  invalidateKeys={[["leave", "types"]]}
                />
              )}
              {isLeaveAdmin && <LeaveTypeForm />}
            </div>
          </div>

          {!isLeaveAdmin && (
            <Alert>
              <AlertTitle>Read-only</AlertTitle>
              <AlertDescription>
                Only HR managers and company admins can change leave types.
              </AlertDescription>
            </Alert>
          )}

          <LeaveSettingsCard editable={isLeaveAdmin} />

          {q.isLoading && (
            <div className="space-y-3">
              {["s1", "s2", "s3", "s4"].map((k) => (
                <Skeleton key={k} className="h-20 w-full" />
              ))}
            </div>
          )}

          {q.isError && (
            <Alert variant="destructive">
              <AlertTitle>Couldn&apos;t load leave types</AlertTitle>
              <AlertDescription>
                {toApiError(q.error).message}
              </AlertDescription>
            </Alert>
          )}

          {q.data && types.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-sm text-muted-foreground">
                No leave types configured yet.
                {isLeaveAdmin &&
                  " Use Restore defaults to seed the standard set."}
              </CardContent>
            </Card>
          )}

          {q.data && types.length > 0 && (
            <div className="space-y-3">
              {types.map((t) => (
                <Card key={t.id}>
                  <CardHeader>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <CardTitle className="text-base">
                          {t.name}{" "}
                          <span className="text-muted-foreground">
                            ({t.code})
                          </span>
                        </CardTitle>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {t.unlimited
                            ? "Unlimited balance"
                            : `${fmtDays(t.default_balance_days ?? 0)} / year`}{" "}
                          · {t.is_paid ? "Paid" : "Unpaid"} ·{" "}
                          {t.requires_approval
                            ? "Needs approval"
                            : "Auto-approved"}
                          {t.accrual_frequency !== "none" && (
                            <>
                              {" "}
                              · Accrues {t.accrual_rate_days ?? 0}/
                              {t.accrual_frequency}
                            </>
                          )}
                          {t.carryover_cap_days != null && (
                            <> · Carryover ≤ {t.carryover_cap_days}</>
                          )}
                          {t.encashable && <> · Encashable</>}
                          {t.is_comp_off && <> · Comp-off</>}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <StatusBadge
                          status={t.is_active ? "valid" : "archived"}
                          label={t.is_active ? "Active" : "Inactive"}
                        />
                        {isLeaveAdmin && <LeaveTypeForm existing={t} />}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </SettingsShell>
    </>
  );
}
